/**
 * HowScoringWorks - Interactive tutorial explaining the scoring system.
 * Accessible at /how-it-works (public, no auth required).
 * Referenced from onboarding flow and nav menu.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  TrendingUp, TrendingDown, Shield, Zap, Trophy, Target,
  ChevronRight, ChevronLeft, Coins, AlertTriangle, Star,
  Award, ArrowLeft, Sparkles, BarChart3, Layers, Crown
} from 'lucide-react';

// Scoring tier data from the backend
const GAIN_TIERS = {
  stock: [
    { range: '+0.1% to +1%', pts: 1, color: '#6ee7b7' },
    { range: '+1% to +3%', pts: 2, color: '#34d399' },
    { range: '+3% to +5%', pts: 4, color: '#10b981' },
    { range: '+5% to +10%', pts: 7, color: '#059669' },
    { range: '+10% to +20%', pts: 12, color: '#047857' },
    { range: '+20%+', pts: 20, color: '#065f46' },
  ],
  crypto: [
    { range: '+0.1% to +1%', pts: 1, color: '#93c5fd' },
    { range: '+1% to +3%', pts: 2, color: '#60a5fa' },
    { range: '+3% to +5%', pts: 3, color: '#3b82f6' },
    { range: '+5% to +10%', pts: 5, color: '#2563eb' },
    { range: '+10% to +20%', pts: 8, color: '#1d4ed8' },
    { range: '+20%+', pts: 12, color: '#1e40af' },
  ],
};

const LOSS_TIERS = [
  { range: '-0.1% to -1%', pts: 0, color: '#fca5a5' },
  { range: '-1% to -3%', pts: -1, color: '#f87171' },
  { range: '-3% to -5%', pts: -2, color: '#ef4444' },
  { range: '-5% to -10%', pts: -4, color: '#dc2626' },
  { range: '-10% to -20%', pts: -7, color: '#b91c1c' },
  { range: '-20%+', pts: -12, color: '#991b1b' },
];

const DIVIDEND_TIERS = [
  { range: '0% - 1%', pts: 1 },
  { range: '1% - 2%', pts: 2 },
  { range: '2% - 3%', pts: 3 },
  { range: '3% - 5%', pts: 4 },
  { range: '5%+', pts: 5 },
];

// Step content for the walkthrough
const STEPS = [
  {
    id: 'overview',
    title: 'How Scoring Works',
    subtitle: 'Your weekly score is calculated from 4 components',
    icon: Trophy,
    color: '#eab308',
  },
  {
    id: 'base',
    title: 'Base Performance',
    subtitle: 'Points from each instrument\'s price movement',
    icon: TrendingUp,
    color: '#10b981',
  },
  {
    id: 'bonuses',
    title: 'Bonuses & Penalties',
    subtitle: 'Rewards for smart play, penalties for risky moves',
    icon: Star,
    color: '#f59e0b',
  },
  {
    id: 'powerplay',
    title: 'Power Play',
    subtitle: 'Your secret weapon — 3x points on one pick',
    icon: Zap,
    color: '#8b5cf6',
  },
  {
    id: 'strategy',
    title: 'Strategy Tips',
    subtitle: 'How the top players stay on top',
    icon: Target,
    color: '#06b6d4',
  },
];

function AnimatedBar({ value, maxValue, color, delay = 0 }) {
  const width = Math.min(Math.abs(value) / maxValue * 100, 100);
  return (
    <div
      className="h-6 rounded-r-md transition-all duration-700 ease-out flex items-center justify-end pr-2"
      style={{
        width: `${width}%`,
        minWidth: '24px',
        background: color,
        animationDelay: `${delay}ms`,
        opacity: 0.9,
      }}
    >
      <span className="text-xs font-bold text-white drop-shadow">{value > 0 ? '+' : ''}{value}</span>
    </div>
  );
}

function ScoreFormula() {
  return (
    <div className="flex items-center gap-2 flex-wrap justify-center text-sm mt-6 mb-2">
      {[
        { label: 'Base Performance', color: '#10b981', icon: TrendingUp },
        null,
        { label: 'Bonuses', color: '#f59e0b', icon: Star },
        null,
        { label: 'Penalties', color: '#ef4444', icon: AlertTriangle },
        null,
        { label: 'Power Play', color: '#8b5cf6', icon: Zap },
      ].map((item, i) =>
        item ? (
          <div
            key={item.label}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg"
            style={{ background: `${item.color}15`, border: `1px solid ${item.color}30` }}
          >
            <item.icon size={14} style={{ color: item.color }} />
            <span style={{ color: item.color }} className="font-medium">{item.label}</span>
          </div>
        ) : (
          <span key={`op-${i}`} className="text-xl font-bold text-slate-500">
            {i < 5 ? '+' : '×'}
          </span>
        )
      )}
      <span className="text-xl font-bold text-slate-500">=</span>
      <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-yellow-500/15 border border-yellow-500/30">
        <Trophy size={14} className="text-yellow-400" />
        <span className="text-yellow-400 font-bold">Weekly Score</span>
      </div>
    </div>
  );
}

export default function HowScoringWorks() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState('stock');

  const currentStep = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="how-scoring-page">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-yellow-900/10 to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-4 pt-6 relative">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="mb-4 text-slate-400 hover:text-white"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4">
        {/* Step indicator */}
        <div className="flex gap-1.5 mb-8 justify-center">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setStep(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === step ? 'w-10' : 'w-2 hover:w-4'
              }`}
              style={{ background: i === step ? currentStep.color : '#334155' }}
              data-testid={`step-dot-${s.id}`}
            />
          ))}
        </div>

        {/* Step header */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ background: `${currentStep.color}15`, border: `2px solid ${currentStep.color}30` }}
          >
            <currentStep.icon size={32} style={{ color: currentStep.color }} />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">{currentStep.title}</h1>
          <p className="text-slate-400 mt-2">{currentStep.subtitle}</p>
        </div>

        {/* Step content */}
        <div className="min-h-[400px]">
          {step === 0 && (
            <div className="space-y-6" data-testid="step-overview">
              <ScoreFormula />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
                {[
                  { icon: TrendingUp, label: 'Base Performance', desc: 'Each instrument scores based on its weekly price movement', color: '#10b981', pts: 'Up to ±20 pts each' },
                  { icon: Shield, label: 'Holding Bonus', desc: '+1 point per instrument held — fill all 10 slots!', color: '#3b82f6', pts: '+10 max' },
                  { icon: Zap, label: 'Power Play', desc: 'Pick 1 instrument for 3x score multiplier', color: '#8b5cf6', pts: '3x multiplier' },
                  { icon: Coins, label: 'Dividends', desc: 'Bonus points for instruments that pay dividends', color: '#f59e0b', pts: '+1 to +5 pts' },
                  { icon: Layers, label: 'Diversity', desc: 'Mix asset types — all-in-one-class = -5 penalty', color: '#06b6d4', pts: '-5 penalty' },
                  { icon: AlertTriangle, label: 'Trade Penalty', desc: '7 free trades/week. Each extra trade costs -10 pts', color: '#ef4444', pts: '-10 per extra' },
                ].map(item => (
                  <Card key={item.label} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors" data-testid={`overview-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <CardContent className="p-4 flex items-start gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${item.color}15` }}>
                        <item.icon size={20} style={{ color: item.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="font-semibold text-white text-sm">{item.label}</p>
                          <Badge className="text-[10px]" style={{ background: `${item.color}20`, color: item.color, border: `1px solid ${item.color}30` }}>
                            {item.pts}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{item.desc}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="bg-amber-950/20 border-amber-800/30 mt-6">
                <CardContent className="p-4 text-center">
                  <p className="text-amber-400 text-sm">
                    <Sparkles className="inline h-4 w-4 mr-1" />
                    Scores are calculated every <strong>Sunday night</strong>. Make your moves before the window closes!
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6" data-testid="step-base">
              <p className="text-slate-300 text-center text-sm">
                Each instrument earns points based on how much its price moved during the week. Bigger moves = more points.
                Different asset types have different scoring tables.
              </p>

              {/* Asset type toggle */}
              <div className="flex gap-2 justify-center">
                {['stock', 'crypto'].map(type => (
                  <Button
                    key={type}
                    variant={selectedAsset === type ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedAsset(type)}
                    className={selectedAsset === type ? 'bg-emerald-600' : 'border-slate-700'}
                    data-testid={`tier-tab-${type}`}
                  >
                    {type === 'stock' ? 'Stocks / ETFs / Commodities' : 'Crypto'}
                  </Button>
                ))}
              </div>

              {/* Gains table */}
              <div>
                <h3 className="text-emerald-400 font-semibold mb-3 flex items-center gap-2">
                  <TrendingUp size={16} /> Gains
                </h3>
                <div className="space-y-1.5">
                  {GAIN_TIERS[selectedAsset].map((tier, i) => (
                    <div key={tier.range} className="flex items-center gap-3">
                      <span className="text-slate-400 text-xs w-28 text-right flex-shrink-0">{tier.range}</span>
                      <AnimatedBar value={tier.pts} maxValue={20} color={tier.color} delay={i * 100} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Losses table */}
              <div>
                <h3 className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                  <TrendingDown size={16} /> Losses
                </h3>
                <div className="space-y-1.5">
                  {LOSS_TIERS.map((tier, i) => (
                    <div key={tier.range} className="flex items-center gap-3">
                      <span className="text-slate-400 text-xs w-28 text-right flex-shrink-0">{tier.range}</span>
                      <AnimatedBar value={tier.pts} maxValue={12} color={tier.color} delay={i * 100} />
                    </div>
                  ))}
                </div>
              </div>

              <Card className="bg-blue-950/20 border-blue-800/30">
                <CardContent className="p-4">
                  <p className="text-blue-400 text-sm">
                    <BarChart3 className="inline h-4 w-4 mr-1" />
                    <strong>Note:</strong> Crypto gains/losses are scored at a lower rate because crypto is naturally more volatile.
                    A +10% crypto move earns 8 pts vs 12 pts for stocks.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-bonuses">
              <p className="text-slate-300 text-center text-sm mb-6">
                Smart portfolio management earns bonuses. Reckless trading earns penalties.
              </p>

              {/* Bonuses */}
              <h3 className="text-emerald-400 font-semibold flex items-center gap-2">
                <Star size={16} /> Bonuses
              </h3>
              <div className="space-y-2">
                {[
                  { name: 'Holding Bonus', value: '+1 per instrument', detail: 'Hold 10 instruments = +10 points. Encourages a full portfolio.', icon: Target, color: '#3b82f6' },
                  { name: 'Dividend Bonus', value: '+1 to +5', detail: 'Instruments that pay dividends earn bonus points based on yield tier.', icon: Coins, color: '#f59e0b' },
                  { name: 'Stability Bonus', value: '+3', detail: 'Positive week with max drawdown < 2% (non-crypto only).', icon: Shield, color: '#10b981' },
                  { name: 'Recovery Bonus', value: '+4 to +8', detail: 'Bounce back 5%+ or 10%+ after a losing week. Rewards patience.', icon: TrendingUp, color: '#22d3ee' },
                  { name: 'Streak Bonus', value: '+5 / +15', detail: '3 consecutive green weeks = +5. 5 weeks = +15. Consistency pays.', icon: Sparkles, color: '#a855f7' },
                  { name: 'Top Performer', value: '+5 / +3 / +1', detail: 'Finish 1st, 2nd, or 3rd in weekly scoring. Glory and points.', icon: Trophy, color: '#eab308' },
                ].map(b => (
                  <Card key={b.name} className="bg-slate-900/50 border-slate-800">
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: `${b.color}15` }}>
                        <b.icon size={16} style={{ color: b.color }} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm font-medium">{b.name}</span>
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">{b.value}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{b.detail}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Penalties */}
              <h3 className="text-red-400 font-semibold flex items-center gap-2 mt-6">
                <AlertTriangle size={16} /> Penalties
              </h3>
              <div className="space-y-2">
                {[
                  { name: 'Extra Trade Penalty', value: '-10 each', detail: '7 free trades per week (1/day). Every trade beyond that costs -10 points.', color: '#ef4444' },
                  { name: 'No Diversity Penalty', value: '-5', detail: '100% in one asset class? -5 points. Mix stocks, crypto, ETFs, and commodities.', color: '#f97316' },
                ].map(p => (
                  <Card key={p.name} className="bg-slate-900/50 border-red-900/20">
                    <CardContent className="p-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-red-500/10">
                        <AlertTriangle size={16} className="text-red-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white text-sm font-medium">{p.name}</span>
                          <Badge className="bg-red-500/20 text-red-400 text-xs">{p.value}</Badge>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{p.detail}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Dividend Tiers */}
              <h3 className="text-amber-400 font-semibold flex items-center gap-2 mt-6">
                <Coins size={16} /> Dividend Yield Tiers
              </h3>
              <div className="flex gap-2 flex-wrap">
                {DIVIDEND_TIERS.map(t => (
                  <div key={t.range} className="px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-center">
                    <p className="text-amber-400 text-xs font-semibold">{t.range}</p>
                    <p className="text-white font-bold">+{t.pts} pt{t.pts > 1 ? 's' : ''}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-powerplay">
              <div className="text-center">
                <div className="inline-flex items-center gap-3 px-6 py-4 rounded-xl bg-violet-500/10 border border-violet-500/20 mb-6">
                  <Zap size={32} className="text-violet-400" />
                  <div className="text-left">
                    <p className="text-violet-400 font-bold text-lg">3x Multiplier</p>
                    <p className="text-slate-400 text-sm">On your chosen instrument</p>
                  </div>
                </div>
              </div>

              <p className="text-slate-300 text-center text-sm">
                Each week, you can designate <strong className="text-violet-400">one instrument</strong> as your Power Play.
                That instrument&apos;s entire score (base + bonuses) is multiplied by 3x.
              </p>

              {/* Example calculation */}
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="p-5">
                  <h4 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <BarChart3 size={16} className="text-violet-400" /> Example: AAPL as Power Play
                  </h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-800">
                      <span className="text-slate-400 text-sm">AAPL weekly gain</span>
                      <span className="text-emerald-400">+4.5%</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-800">
                      <span className="text-slate-400 text-sm">Base points (stock +3%-5%)</span>
                      <span className="text-white">4 pts</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-800">
                      <span className="text-slate-400 text-sm">Dividend bonus (+2.1% yield)</span>
                      <span className="text-amber-400">+3 pts</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-violet-500/30">
                      <span className="text-slate-400 text-sm">Subtotal before multiplier</span>
                      <span className="text-white font-medium">7 pts</span>
                    </div>
                    <div className="flex justify-between items-center py-2 bg-violet-500/10 rounded-lg px-3">
                      <span className="text-violet-400 font-semibold flex items-center gap-1">
                        <Zap size={14} /> Power Play 3x
                      </span>
                      <span className="text-violet-300 font-bold text-lg">= 21 pts</span>
                    </div>
                  </div>
                  <p className="text-slate-500 text-xs mt-3 text-center">
                    Without Power Play, AAPL would score 7 pts. With it: 21 pts!
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-amber-950/20 border-amber-800/30">
                <CardContent className="p-4">
                  <p className="text-amber-400 text-sm">
                    <AlertTriangle className="inline h-4 w-4 mr-1" />
                    <strong>Warning:</strong> Power Play works both ways! If your pick drops -5%, you lose 3x the points too.
                    Choose wisely.
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4" data-testid="step-strategy">
              {[
                {
                  title: 'Fill all 10 slots',
                  tip: 'The +10 holding bonus is free points every week. Always have a full portfolio before scoring closes.',
                  icon: Target, color: '#3b82f6'
                },
                {
                  title: 'Diversify across asset types',
                  tip: 'Mix stocks, crypto, ETFs, and commodities. Avoids the -5 diversity penalty and reduces risk.',
                  icon: Layers, color: '#06b6d4'
                },
                {
                  title: 'Pick your Power Play carefully',
                  tip: 'Choose the instrument you\'re most confident will move up this week. High conviction = high reward.',
                  icon: Zap, color: '#8b5cf6'
                },
                {
                  title: 'Hunt for dividend payers',
                  tip: 'Stocks and ETFs with good dividends earn bonus points every week on top of price gains.',
                  icon: Coins, color: '#f59e0b'
                },
                {
                  title: 'Don\'t over-trade',
                  tip: 'You get 7 free trades per week (1/day). Each extra trade costs -10 points. Be strategic, not reactive.',
                  icon: AlertTriangle, color: '#ef4444'
                },
                {
                  title: 'Consistency beats home runs',
                  tip: 'The streak bonus rewards 3+ consecutive positive weeks. Aim for steady gains, not big gambles.',
                  icon: Award, color: '#10b981'
                },
                {
                  title: 'Study the leaderboard',
                  tip: 'Click on top players to see their portfolios (if public). Learn what the best players are holding.',
                  icon: Crown, color: '#eab308'
                },
              ].map((tip, i) => (
                <Card key={tip.title} className="bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors">
                  <CardContent className="p-4 flex items-start gap-4">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg font-bold"
                      style={{ background: `${tip.color}15`, color: tip.color }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <p className="text-white font-semibold">{tip.title}</p>
                      <p className="text-slate-400 text-sm mt-1">{tip.tip}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}

              <div className="text-center mt-8">
                <Button
                  onClick={() => navigate('/portfolio')}
                  className="bg-emerald-600 hover:bg-emerald-500 px-8"
                  data-testid="start-trading-btn"
                >
                  Start Building Your Portfolio <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between mt-10 pt-6 border-t border-slate-800">
          <Button
            variant="outline"
            onClick={() => setStep(s => s - 1)}
            disabled={isFirst}
            className="border-slate-700 disabled:opacity-30"
            data-testid="prev-step-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>

          <span className="text-slate-500 text-sm">
            {step + 1} of {STEPS.length}
          </span>

          <Button
            onClick={() => isLast ? navigate('/portfolio') : setStep(s => s + 1)}
            className={isLast ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-slate-800 hover:bg-slate-700'}
            data-testid="next-step-btn"
          >
            {isLast ? 'Start Playing' : 'Next'} <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
